// src/pages/hr/recruitment/HRAssignedCandidates.jsx
// ============================================================
// HR Recruitment: HR Final Review & Assigned Candidates Module
// HR executes final recruitment decisions after Team Lead evaluation
// and Admin review.
// Displays complete Candidate Profile, Resume, HR Screening Notes,
// Team Lead Technical Rating & Feedback, Admin Recommendation & Remarks,
// Interview History, and full Audit Trail (Lead -> Admin -> HR).
// Exclusive HR Actions:
//   - Proceed to Next Round
//   - Schedule HR Interview
//   - On Hold
//   - Reject Application
//   - Generate Offer Letter
//   - Send Offer
//   - Move to Onboarding
//   - Mark as Hired
// ============================================================

import React, { useState, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useCandidates, submitHRFinalDecision } from "../../../hooks/useRecruitment";
import CandidateProfile from "./CandidateProfile";
import { toast } from "react-hot-toast";
import {
  Search,
  X,
  FileText,
  Download,
  Calendar,
  UserCheck,
  Award,
  CheckCircle2,
  XCircle,
  PauseCircle,
  Eye,
  Phone,
  Mail,
  ShieldCheck,
  Send,
  UserPlus,
  Clock,
} from "lucide-react";

export default function HRAssignedCandidates() {
  const { userProfile } = useAuth();
  const hrName = userProfile?.displayName || userProfile?.name || "HR Admin";
  const hrId = userProfile?.uid || userProfile?.id || "";

  const { data: candidates, loading } = useCandidates();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");

  // Profile Modal
  const [profileModalCandidate, setProfileModalCandidate] = useState(null);

  // HR Decision Modal
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [hrAction, setHrAction] = useState("Move to Onboarding");
  const [hrRemarks, setHrRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Unique departments for filter
  const departments = useMemo(() => {
    const set = new Set();
    candidates.forEach((c) => {
      if (c.department) set.add(c.department);
    });
    return Array.from(set);
  }, [candidates]);

  // Candidates for HR Final Review:
  // Show candidates that have passed Admin review or are in HR final stages
  const hrCandidates = useMemo(() => {
    return candidates.filter((c) => {
      const isRelevant =
        c.stage === "Pending HR Final Review" ||
        c.adminDecision ||
        c.hrFinalAction ||
        c.stage === "Moved to Onboarding" ||
        c.stage === "Hired" ||
        (c.leadRecommendation && c.adminDecision);

      if (!isRelevant) return false;

      const matchSearch =
        (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.email || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.appliedPosition || c.role || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.assignedLeadName || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.adminReviewedBy || "").toLowerCase().includes(search.toLowerCase());

      const matchDept = deptFilter === "all" || c.department === deptFilter;

      const matchStatus =
        statusFilter === "all" ||
        (statusFilter === "pending" && (c.stage === "Pending HR Final Review" || !c.hrFinalAction || c.stage !== "Moved to Onboarding")) ||
        (statusFilter === "Onboarding" && (c.stage === "Moved to Onboarding" || c.stage === "Hired" || c.hrFinalAction === "Move to Onboarding"));

      return matchSearch && matchDept && matchStatus;
    });
  }, [candidates, search, deptFilter, statusFilter]);

  // Key Statistics
  const stats = useMemo(() => {
    const pending = candidates.filter((c) => c.stage === "Pending HR Final Review" || (c.adminDecision && !c.hrFinalAction && c.stage !== "Moved to Onboarding")).length;
    const onboarded = candidates.filter((c) => c.stage === "Moved to Onboarding" || c.stage === "Hired" || c.hrFinalAction === "Move to Onboarding").length;
    return { pending, onboarded };
  }, [candidates]);

  function handleOpenDecisionModal(c) {
    setSelectedCandidate(c);
    setHrAction("Move to Onboarding");
    setHrRemarks(c.hrFinalRemarks || "");
    setShowDecisionModal(true);
  }

  async function handleDecisionSubmit(e) {
    e.preventDefault();
    if (!selectedCandidate) return;
    setSubmitting(true);

    try {
      await submitHRFinalDecision(selectedCandidate.id, {
        hrAction: "Move to Onboarding",
        hrRemarks,
        hrName,
        hrId,
      });

      toast.success(`${selectedCandidate.name} has been moved to Onboarding!`);
      setShowDecisionModal(false);
      setSelectedCandidate(null);
    } catch (err) {
      toast.error("Failed to move candidate to Onboarding: " + err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .hr-cand-header {
          background: #1b4b31;
          border-radius: 16px;
          padding: 1.35rem 1.75rem;
          color: #ffffff;
          box-shadow: 0 4px 20px rgba(27, 75, 49, 0.15);
        }
        .hr-stat-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
        }
        .hr-stat-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 1.1rem 1.25rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
          display: flex;
          align-items: center;
          gap: 1rem;
          transition: all 0.2s ease;
        }
        .hr-stat-card:hover {
          border-color: #10b981;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.08);
        }
        .hr-stat-icon {
          width: 44px; height: 44px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
          background: #f0fdf4;
          border: 1.5px solid #bbf7d0;
          color: #15803d;
        }

        .hr-cand-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 1.25rem;
        }
        .hr-cand-card {
          background: var(--bg-card, #ffffff);
          border: 1px solid var(--border, #e2e8f0);
          border-radius: 16px;
          padding: 1.35rem;
          box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
          display: flex;
          flex-direction: column;
          gap: 1rem;
          transition: all 0.2s ease-in-out;
        }
        .hr-cand-card:hover {
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
          border-color: #cbd5e1;
        }

        .audit-timeline-box {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 0.85rem 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }
        .audit-step-row {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
          font-size: 0.76rem;
        }
        .audit-step-dot {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-weight: 800;
          font-size: 0.68rem;
          color: #ffffff;
        }

        /* Modal styling matching HR Job Opening layout */
        .modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-color: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1.25rem;
          box-sizing: border-box;
        }
        .modal-body {
          background: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 680px;
          max-height: 90vh;
          overflow-y: auto;
          padding: 1.75rem 2rem;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.15);
        }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 1.1rem; border-bottom: 1px solid #f1f5f9; margin-bottom: 1.25rem; }
        .modal-title-group { display: flex; align-items: center; gap: 0.75rem; }
        .modal-title-icon { width: 40px; height: 40px; border-radius: 10px; background: #e6f4ed; color: #1b4b31; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .modal-title { margin: 0; font-size: 1.15rem; font-weight: 700; color: #0f172a; }
        .modal-subtitle { margin: 3px 0 0 0; font-size: 0.78rem; color: #64748b; }
        .modal-close-btn {
          display: flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 50%;
          border: 1px solid #e2e8f0; background: #f8fafc; color: #64748b;
          cursor: pointer; transition: all 0.15s ease; padding: 0;
        }
        .modal-close-btn:hover { background: #fee2e2; color: #ef4444; border-color: #fca5a5; }

        .action-card-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 0.65rem;
        }
        .action-card-btn {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.35rem;
          padding: 0.75rem 0.85rem;
          border-radius: 10px;
          border: 1.5px solid #e2e8f0;
          background: #ffffff;
          color: #334155;
          font-weight: 700;
          font-size: 0.78rem;
          cursor: pointer;
          transition: all 0.18s ease;
          text-align: left;
        }
        .action-card-btn:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }
        .action-card-btn.selected {
          border-color: #1b4b31;
          background: #e6f4ed;
          color: #1b4b31;
          box-shadow: 0 4px 12px rgba(27, 75, 49, 0.12);
        }
      `}</style>

      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "1rem",
        marginBottom: "1.25rem"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <Award size={24} strokeWidth={1.8} color="#1b4b31" />
          <h1 style={{ fontSize: "1.5rem", fontWeight: 800, margin: 0, letterSpacing: "-0.02em", color: "#1b4b31" }}>
            HR Final Recruitment Review
          </h1>
        </div>
      </div>

      {/* KPI Stats Bar */}
      <div className="hr-stat-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <div className="hr-stat-card">
          <div className="hr-stat-icon">
            <Clock className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div>
            <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{stats.pending}</div>
            <div style={{ fontSize: "0.78rem", color: "#475569", fontWeight: 600 }}>Pending HR Review</div>
          </div>
        </div>

        <div className="hr-stat-card">
          <div className="hr-stat-icon" style={{ background: "#ecfdf5", borderColor: "#a7f3d0", color: "#059669" }}>
            <UserPlus className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div>
            <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0f172a" }}>{stats.onboarded}</div>
            <div style={{ fontSize: "0.78rem", color: "#475569", fontWeight: 600 }}>Moved to Onboarding</div>
          </div>
        </div>
      </div>

      {/* Redesigned ATS Toolbar */}
      <div style={{ background: "var(--bg-card, #ffffff)", border: "1px solid var(--border, #e2e8f0)", borderRadius: 14, padding: "0.85rem 1.1rem", display: "flex", gap: "0.85rem", alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
        {/* Full-width search box */}
        <div style={{ position: "relative", display: "flex", alignItems: "center", flex: 1, minWidth: 280, background: "#ffffff", border: "1.5px solid #cbd5e1", borderRadius: 10, padding: "0 0.85rem", height: 42 }}>
          <Search size={18} style={{ color: "#64748b", marginRight: "0.65rem", flexShrink: 0 }} />
          <input
            type="text"
            style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontSize: "0.86rem", fontWeight: 500, color: "#0f172a" }}
            placeholder="Search candidate, position, lead, or admin..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              style={{ border: "none", background: "#e2e8f0", color: "#475569", width: 20, height: 20, borderRadius: "50%", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              onClick={() => setSearch("")}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Department filter */}
        <select
          className="ats-select"
          style={{ height: 42, padding: "0 0.85rem", border: "1.5px solid #cbd5e1", borderRadius: 10, fontSize: "0.85rem", minWidth: 170 }}
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
        >
          <option value="all">All Departments</option>
          {departments.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        {/* Status filter */}
        <select
          className="ats-select"
          style={{ height: 42, padding: "0 0.85rem", border: "1.5px solid #cbd5e1", borderRadius: 10, fontSize: "0.85rem", minWidth: 180 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Candidates</option>
          <option value="pending">Pending HR Review</option>
          <option value="Onboarding">Moved to Onboarding</option>
        </select>
      </div>

      {/* Candidate Grid */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "4rem 2rem", color: "var(--text-muted)" }}>
          <div className="hrms-spinner" style={{ margin: "0 auto 1rem" }} />
          <p>Loading candidate reviews...</p>
        </div>
      ) : hrCandidates.length === 0 ? (
        <div style={{ background: "var(--bg-card, #ffffff)", border: "2px dashed var(--border, #cbd5e1)", borderRadius: 16, padding: "4rem 2rem", textAlign: "center", color: "var(--text-muted)" }}>
          <UserCheck className="w-12 h-12 text-slate-300" style={{ margin: "0 auto 1rem" }} />
          <h3 style={{ fontSize: "1.1rem", fontWeight: 700, margin: "0 0 0.5rem", color: "#334155" }}>No Candidates Pending HR Review</h3>
          <p style={{ fontSize: "0.85rem", margin: 0 }}>
            {search || statusFilter !== "all" || deptFilter !== "all"
              ? "No candidate evaluations match your selected filters."
              : "When Admins save decision recommendations, candidate profiles will appear here for HR final recruitment actions."}
          </p>
        </div>
      ) : (
        <div className="hr-cand-grid">
          {hrCandidates.map((c) => {
            const ratingScore = Number(c.technicalRating || 8);
            const isPending = c.stage === "Pending HR Final Review" || !c.hrFinalAction;

            return (
              <div key={c.id} className="hr-cand-card">
                {/* Header Row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                  <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#1b4b31", color: "#ffffff", fontWeight: 800, fontSize: "1rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {c.name?.slice(0, 2)?.toUpperCase() || "CN"}
                    </div>
                    <div>
                      <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "#0f172a" }}>{c.name}</div>
                      <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600 }}>
                        {c.appliedPosition || c.role || "Position"} · <span style={{ color: "#1b4b31" }}>{c.department || "General"}</span>
                      </div>
                    </div>
                  </div>

                  <span style={{ padding: "0.25rem 0.75rem", borderRadius: 99, fontSize: "0.74rem", fontWeight: 700, background: isPending ? "#fef3c7" : "#d1fae5", color: isPending ? "#92400e" : "#065f46", border: `1px solid ${isPending ? "#fde68a" : "#a7f3d0"}` }}>
                    {c.hrFinalAction ? c.hrFinalAction : "Pending HR Decision"}
                  </span>
                </div>

                {/* Contact Row */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", fontSize: "0.76rem", color: "#475569", borderBottom: "1px solid #f1f5f9", paddingBottom: "0.75rem" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                    <Mail className="w-3.5 h-3.5 text-slate-400" /> {c.email || "No email"}
                  </span>
                  {c.phone && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
                      <Phone className="w-3.5 h-3.5 text-slate-400" /> {c.phone}
                    </span>
                  )}
                </div>

                {/* Resume Row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", padding: "0.6rem 0.85rem", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.78rem", fontWeight: 600, color: "#334155" }}>
                    <FileText className="w-4 h-4 text-emerald-700" /> Candidate Resume
                  </div>
                  {c.resumeUrl ? (
                    <div style={{ display: "flex", gap: "0.4rem" }}>
                      <a href={c.resumeUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", padding: "0.25rem 0.6rem", fontSize: "0.72rem", fontWeight: 700, borderRadius: 6, background: "#ffffff", border: "1px solid #cbd5e1", color: "#0f172a", textDecoration: "none" }}>
                        <Eye className="w-3 h-3 text-emerald-700" /> View
                      </a>
                      <a href={c.resumeUrl} download style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", padding: "0.25rem 0.6rem", fontSize: "0.72rem", fontWeight: 700, borderRadius: 6, background: "#1b4b31", color: "#ffffff", textDecoration: "none" }}>
                        <Download className="w-3 h-3" /> Download
                      </a>
                    </div>
                  ) : (
                    <span style={{ fontSize: "0.74rem", color: "#94a3b8", fontStyle: "italic" }}>Resume on file</span>
                  )}
                </div>

                {/* Audit Trail Timeline */}
                <div className="audit-timeline-box">
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    RECRUITMENT AUDIT TRAIL
                  </div>

                  {/* Step 1: HR Screening */}
                  <div className="audit-step-row">
                    <div className="audit-step-dot" style={{ background: "#059669" }}>1</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>HR Resume Screening</div>
                      <div style={{ color: "#64748b" }}>{c.hrScreeningNotes || "Shortlisted for technical round"}</div>
                    </div>
                  </div>

                  {/* Step 2: Team Lead Technical Evaluation */}
                  <div className="audit-step-row">
                    <div className="audit-step-dot" style={{ background: "#10b981" }}>2</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: "#0f172a", display: "flex", justifyContent: "space-between" }}>
                        <span>Lead Technical Eval ({c.assignedLeadName || c.leadReviewedBy || "Lead"})</span>
                        <span style={{ color: "#166534" }}>{ratingScore}/10</span>
                      </div>
                      <div style={{ color: "#64748b" }}>
                        Rec: <strong>{c.leadRecommendation || "Proceed"}</strong> · "{c.technicalFeedback || c.leadComments || "Cleared technical assessment"}"
                      </div>
                    </div>
                  </div>

                  {/* Step 3: Admin Review */}
                  <div className="audit-step-row">
                    <div className="audit-step-dot" style={{ background: "#047857" }}>3</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: "#0f172a", display: "flex", justifyContent: "space-between" }}>
                        <span>Admin Review ({c.adminReviewedBy || "Admin"})</span>
                        <span style={{ color: "#065f46" }}>{c.adminDecision || "Proceed to Next Round"}</span>
                      </div>
                      <div style={{ color: "#64748b" }}>{c.adminRemarks ? `Remarks: "${c.adminRemarks}"` : "Approved for HR final decision"}</div>
                    </div>
                  </div>

                  {/* Step 4: HR Final Action */}
                  <div className="audit-step-row">
                    <div className="audit-step-dot" style={{ background: c.hrFinalAction ? "#1b4b31" : "#94a3b8" }}>4</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>
                        HR Final Action: <span style={{ color: "#1b4b31" }}>{c.hrFinalAction || "Pending HR Execution"}</span>
                      </div>
                      {c.hrFinalRemarks && <div style={{ color: "#64748b" }}>Feedback: "{c.hrFinalRemarks}"</div>}
                    </div>
                  </div>
                </div>

                {/* Card Footer Actions */}
                <div style={{ display: "flex", gap: "0.5rem", marginTop: "auto", paddingTop: "0.5rem" }}>
                  <button
                    className="btn-secondary"
                    style={{ flex: 1, padding: "0.55rem 0.75rem", fontSize: "0.78rem", fontWeight: 700, borderRadius: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.35rem" }}
                    onClick={() => setProfileModalCandidate(c)}
                  >
                    <Eye className="w-3.5 h-3.5" /> Full Profile
                  </button>

                  <button
                    className="btn-primary"
                    style={{ flex: 1.3, height: 40, padding: "0 0.85rem", fontSize: "0.78rem", justifyContent: "center" }}
                    onClick={() => handleOpenDecisionModal(c)}
                  >
                    <UserPlus size={16} /> Move to Onboarding
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Interactive Move to Onboarding Modal */}
      {showDecisionModal && selectedCandidate && (
        <div className="modal-overlay" onClick={() => setShowDecisionModal(false)}>
          <div className="modal-body" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-title-icon" style={{ background: "#ecfdf5", color: "#059669" }}>
                  <UserPlus size={22} />
                </div>
                <div>
                  <h3 className="modal-title">Move Candidate to Onboarding</h3>
                  <p className="modal-subtitle">
                    Candidate: <strong>{selectedCandidate.name}</strong> · Position: <strong>{selectedCandidate.appliedPosition || selectedCandidate.role}</strong>
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowDecisionModal(false)}
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Evaluation & Admin Summary Box */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "0.85rem 1rem", marginBottom: "1.25rem", fontSize: "0.8rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <div style={{ background: "#ffffff", padding: "0.6rem 0.85rem", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                <div style={{ fontWeight: 700, color: "#1b4b31", fontSize: "0.74rem", marginBottom: 3 }}>TEAM LEAD EVALUATION</div>
                <div>Rating: <strong>{selectedCandidate.technicalRating || 8}/10</strong></div>
                <div>Rec: <strong>{selectedCandidate.leadRecommendation || "Proceed"}</strong></div>
              </div>

              <div style={{ background: "#ffffff", padding: "0.6rem 0.85rem", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                <div style={{ fontWeight: 700, color: "#6d28d9", fontSize: "0.74rem", marginBottom: 3 }}>ADMIN REVIEW</div>
                <div>Decision: <strong>{selectedCandidate.adminDecision || "Proceed to Next Round"}</strong></div>
                <div>Admin: <strong>{selectedCandidate.adminReviewedBy || "Admin"}</strong></div>
              </div>
            </div>

            {/* HR Decision Form */}
            <form onSubmit={handleDecisionSubmit} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              {/* HR Action Choice Card */}
              <div>
                <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "#0f172a", marginBottom: "0.6rem" }}>
                  Selected HR Action <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div
                  style={{
                    background: "#ecfdf5",
                    border: "2px solid #10b981",
                    borderRadius: 12,
                    padding: "1rem 1.25rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.9rem",
                    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.08)",
                  }}
                >
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 10,
                      background: "#10b981",
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <UserPlus size={22} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, color: "#065f46", fontSize: "0.95rem" }}>
                      Move to Onboarding
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#047857", marginTop: 2 }}>
                      Candidate will be transferred to the HR Onboarding section with a new joiner profile and checklist.
                    </div>
                  </div>
                </div>
              </div>

              {/* HR Remarks Textarea */}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "#0f172a" }}>
                  HR Onboarding Notes & Guidelines
                </label>
                <textarea
                  className="ats-input"
                  rows={3}
                  style={{ width: "100%", boxSizing: "border-box", borderRadius: 10, padding: "0.75rem", fontSize: "0.86rem", resize: "vertical", height: 90 }}
                  placeholder="Enter any onboarding guidelines, department assignment details, or notes..."
                  value={hrRemarks}
                  onChange={(e) => setHrRemarks(e.target.value)}
                />
              </div>

              {/* Modal Footer Buttons */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", paddingTop: "0.75rem", borderTop: "1px solid #f1f5f9" }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ borderRadius: 9, padding: "0.6rem 1.25rem", height: 42 }}
                  onClick={() => setShowDecisionModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  style={{ height: 42, padding: "0 1.4rem", display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
                  disabled={submitting}
                >
                  <UserPlus size={18} /> {submitting ? "Moving to Onboarding..." : "Move to Onboarding"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Candidate Profile Modal */}
      {profileModalCandidate && (
        <CandidateProfile
          candidate={profileModalCandidate}
          onClose={() => setProfileModalCandidate(null)}
        />
      )}
    </div>
  );
}
